import { Button, Box, Divider } from '@mui/material';
import { observer } from 'mobx-react-lite';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useStore } from 'logic';
import { isWholeViewEnabled } from 'logic/flags';

export type ProjectTab = 'home' | 'queue' | 'library' | 'assets' | 'status' | 'notes' | 'whole';

interface ProjectTabsProps {
  activeTab: ProjectTab | null;
  onTabChange: (tab: ProjectTab) => void;
}

export const ProjectTabs = observer(({ activeTab, onTabChange }: ProjectTabsProps) => {
  const { uiActiveSessionStore, uiCardSearchStore, uiModulePowerColumnStore } = useStore();

  // Add null checks to prevent errors when stores aren't initialized
  if (!uiActiveSessionStore || !uiCardSearchStore || !uiModulePowerColumnStore) {
    return null;
  }

  const { shouldShowQueueFilter } = uiActiveSessionStore;
  const { currentSearch } = uiCardSearchStore;
  const { rowSelectionsByModuleId } = uiModulePowerColumnStore;

  // Calculate filter count
  const searchTerms = currentSearch
    ? currentSearch
        .split(',')
        .map((term) => term.trim())
        .filter((term) => term.length > 0)
    : [];
  const numActiveFilters =
    Object.values(rowSelectionsByModuleId).reduce(
      (total, selections) => total + selections.size,
      0,
    ) + searchTerms.length;

  // The three jobs, in spine order: PLAN -> PRODUCE -> (DISTRIBUTE).
  // PLAN leads the bar: the campaign-wide "god view" where structure is mapped.
  const journeyTabs: { id: ProjectTab; label: string }[] = [
    ...(isWholeViewEnabled() ? [{ id: 'whole' as ProjectTab, label: 'PLAN' }] : []),
    { id: 'queue', label: 'PRODUCE' },
    // 'assets' holds the third job; becomes DISTRIBUTE once it earns the label.
    { id: 'assets', label: 'ASSETS' },
  ];

  // Utilities — things you reach for, not destinations. Set apart from the journey.
  const utilityTabs: { id: ProjectTab; label: string }[] = [
    { id: 'library', label: 'SOURCES' },
    { id: 'home', label: 'SEARCH' },
    // { id: 'status', label: 'METRICS' }, // Temporarily hidden
    // { id: 'notes', label: 'PRIVATE NOTES' }, // Temporarily hidden - not getting much use
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flex: '0 1 auto',
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        // Smooth scrolling
        scrollBehavior: 'smooth',
        // Hide scrollbar but keep functionality
        '&::-webkit-scrollbar': {
          height: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
          },
        },
        // For Firefox
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
      }}
    >
      {journeyTabs.map(renderTab)}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />
      {utilityTabs.map(renderTab)}
    </Box>
  );

  function renderTab(tab: { id: ProjectTab; label: string }) {
    const isActive = activeTab === tab.id;
    const showFilterIcon = tab.id === 'queue' && shouldShowQueueFilter;

    return (
      <Button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        size="small"
        sx={{
          minWidth: 'auto',
          px: 1.25,
          py: 0.5,
          fontSize: '0.8125rem',
          textTransform: 'none',
          borderRadius: 2,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          // Active tab: light background, dark text
          ...(isActive
            ? {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: 'rgba(0, 0, 0, 0.87)',
                fontWeight: 400,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                },
              }
            : {
                // Inactive tab: very dim background, dim text
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                color: 'rgba(255, 255, 255, 0.4)',
                fontWeight: 400,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }),
        }}
      >
        {tab.label}
        {showFilterIcon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              ml: 0.25,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                opacity: isActive ? 0.7 : 0.6,
              }}
            >
              {numActiveFilters}
            </Box>
            <FilterListIcon
              sx={{
                fontSize: '0.875rem',
                opacity: isActive ? 0.7 : 0.6,
              }}
            />
          </Box>
        )}
      </Button>
    );
  }
});
